import {chmod, mkdir, readFile, writeFile} from 'fs/promises';
import os from 'os';
import path from 'path';

import {handleError} from '@/utilities/errors';

const CONFIG_KEYS = ['gitlabUrl', 'gitlabToken'] as const;

export type Config = {
  [K in (typeof CONFIG_KEYS)[number]]: string | undefined;
};

export type Configured = {
  [K in (typeof CONFIG_KEYS)[number]]: string;
};

let configCache: Config | null = null;

export const getEnvConfig = () => ({
  gitlabUrl: process.env.GITLAB_URL ?? undefined,
  gitlabToken: process.env.GITLAB_TOKEN ?? undefined,
});

export const clearCache = () => {
  configCache = null;
};

export const getConfigPaths = () => {
  const configDir = process.env.CONFIG_DIR?.replace(/^~/, os.homedir()) ?? os.homedir();
  const configPath = path.join(configDir, 'config');
  return {
    configDir,
    configPath,
  };
};

export const getConfigFile = () => getConfigPaths().configPath;

export const getConfigDir = () => getConfigPaths().configDir;

const isConfigComplete = (config: Config) => CONFIG_KEYS.every(key => config[key] !== undefined);

export const getConfig = async (refreshConfigCache: boolean = false) => {
  if (refreshConfigCache) clearCache();

  const envConfig = getEnvConfig();

  if (!configCache) {
    try {
      const config = await readFile(getConfigFile(), 'utf8');
      configCache = JSON.parse(config);
    } catch (error) {
      if (isConfigComplete(envConfig)) {
        return envConfig as Configured;
      }
      throw error;
    }
  }

  return CONFIG_KEYS.reduce((acc, key) => {
    if (envConfig[key] !== undefined) {
      return {
        ...acc,
        [key]: envConfig[key],
      };
    }
    if (configCache && configCache[key] !== undefined) {
      return {
        ...acc,
        [key]: configCache[key],
      };
    }
    return acc;
  }, {} as Configured);
};

export const getConfigOrExit = async (refreshConfigCache: boolean = false) => {
  try {
    return await getConfig(refreshConfigCache);
  } catch (error: any) {
    if (error.code === 'ENOENT') {
      handleError('Token configuration not found. Please run the configure command first.');
    } else {
      handleError(`An error occurred while reading the token:${error.message}`);
    }
    process.exit(1);
  }

  return {} as Configured;
};

export const saveConfig = async (config: Configured) => {
  const {configDir, configPath} = getConfigPaths();

  await mkdir(configDir, {recursive: true});
  await writeFile(configPath, JSON.stringify(config), 'utf8');
  await chmod(configPath, 0o600);
};
